import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { getLocale } from '@vendasta/galaxy/i18n';
import { localizedWeekOrder } from '@vendasta/galaxy/utility/date-utils';
import { DayOfWeek, RegularHoursPeriod } from '@vendasta/listing-products';
import dayjs from 'dayjs';
import _ from 'lodash';
import { HoursOfOperation } from '../../hours-of-operation/hours-of-operation';
import { TimeSpan } from '../../hours-of-operation/hours-of-operation.interface';
import { timeOfDayToTimeString, timeSpanToRegularHoursPeriod } from '../business-hours';

@Component({
  selector: 'forms-edit-regular-hours-dialog',
  templateUrl: './edit-regular-hours-dialog.component.html',
  styleUrls: ['./edit-regular-hours-dialog.component.scss'],
  standalone: false,
})
export class EditRegularHoursDialogComponent implements OnInit {
  @Input() control: UntypedFormControl;
  @Input() days: Map<DayOfWeek, TimeSpan[]> = new Map<DayOfWeek, TimeSpan[]>();
  @Input() customTitle = '';
  @Input() showRemoveButton = false;

  DayOfWeek = DayOfWeek;
  daysInWeek: DayOfWeek[] = [];

  validationErrorTimePickerComponent = false;
  errorStatusForEachDay = new Array(8).fill(false);

  constructor(
    public dialogRef: MatDialogRef<EditRegularHoursDialogComponent>,
    private cdr: ChangeDetectorRef,
  ) {
    this.days[DayOfWeek.SUNDAY] = [];
    this.days[DayOfWeek.MONDAY] = [];
    this.days[DayOfWeek.TUESDAY] = [];
    this.days[DayOfWeek.WEDNESDAY] = [];
    this.days[DayOfWeek.THURSDAY] = [];
    this.days[DayOfWeek.FRIDAY] = [];
    this.days[DayOfWeek.SATURDAY] = [];

    const locale = getLocale();
    this.daysInWeek = localizedWeekOrder({ code: locale }).map((day) => {
      return {
        0: DayOfWeek.SUNDAY,
        1: DayOfWeek.MONDAY,
        2: DayOfWeek.TUESDAY,
        3: DayOfWeek.WEDNESDAY,
        4: DayOfWeek.THURSDAY,
        5: DayOfWeek.FRIDAY,
        6: DayOfWeek.SATURDAY,
      }[day];
    });
  }

  ngOnInit(): void {
    const value: RegularHoursPeriod[] = this.control.value;
    value?.forEach((period) => {
      const existing = this.days.get(period.openDay) || [];
      existing.push({ opens: timeOfDayToTimeString(period.openTime), closes: timeOfDayToTimeString(period.closeTime) });
      this.days.set(period.openDay, existing);
    });
  }

  isSpanValid(span: TimeSpan): boolean {
    return !!(span.closes && span.opens);
  }

  valueChanged(day: DayOfWeek, timeSchedule: HoursOfOperation): void {
    this.days.set(day, timeSchedule);
    this.cdr.detectChanges();
  }

  save(): void {
    const result: RegularHoursPeriod[] = [];
    for (const day of this.daysInWeek) {
      const dayTimeSpan = this.days.get(day);
      if (dayTimeSpan?.length > 0) {
        for (const span of dayTimeSpan) {
          if (!this.isSpanValid(span)) {
            continue;
          }

          result.push(timeSpanToRegularHoursPeriod(span, day));
        }
      }
    }
    this.dialogRef.close(result);
  }

  remove(): void {
    this.dialogRef.close([]);
  }

  handleHasAnyErrors(error: boolean, i: number): void {
    if (this.errorStatusForEachDay[i] !== error) {
      this.errorStatusForEachDay[i] = error;
    }
    const errorOnAnyDay = this.errorStatusForEachDay.indexOf(true) !== -1;
    if (errorOnAnyDay !== this.validationErrorTimePickerComponent) {
      this.validationErrorTimePickerComponent = errorOnAnyDay;
      this.cdr.detectChanges();
    }
  }

  // Check if the time range crosses midnight so we can split it
  private isCrossMidnight(closeTime: dayjs.Dayjs, openTime: dayjs.Dayjs): boolean {
    // Can only uplift from openTime/closeTime hours to All Day if set to 12:00AM and 12:00AM
    if (closeTime.hour() === 0 && closeTime.minute() === 0) {
      return false;
    }

    // If closing is before opening
    return closeTime.isBefore(openTime);
  }

  private isClosingMidnight(closeTime: dayjs.Dayjs) {
    return closeTime.hour() === 0 && closeTime.minute() === 0;
  }

  private parseCrossMidnightHours(timeSpanList: TimeSpan[]): TimeSpan[] {
    return timeSpanList.reduce((acc: TimeSpan[], currentTimeSpan: TimeSpan) => {
      const openTime = this.convertTimeToDayJs(currentTimeSpan.opens);
      const closeTime = this.convertTimeToDayJs(currentTimeSpan.closes);
      if (this.isCrossMidnight(closeTime, openTime)) {
        let splitTime: TimeSpan[] = [];
        const crossMidnightTimeSpan = {
          opens: '24:00',
          closes: currentTimeSpan.closes,
        };

        const preMidnightTimeSpan = {
          opens: currentTimeSpan.opens,
          closes: '24:00',
        };

        if (!this.timeSpanExists(timeSpanList, crossMidnightTimeSpan)) {
          splitTime = [...splitTime, crossMidnightTimeSpan];
        }

        if (!this.timeSpanExists(timeSpanList, preMidnightTimeSpan)) {
          splitTime = [...splitTime, preMidnightTimeSpan];
        }

        return [...acc, ...splitTime];
      }

      if (this.isClosingMidnight(closeTime)) {
        currentTimeSpan.closes = '24:00';
      }

      return [...acc, currentTimeSpan];
    }, []);
  }

  // Check if the time span already exists within the specific day.
  timeSpanExists(timeSpanList: TimeSpan[], newTimeSpan: TimeSpan): boolean {
    const existingTimeSpan = _.filter(timeSpanList, (tSpan: TimeSpan) => {
      const tsOpenTime = this.convertTimeToDayJs(tSpan.opens);
      const tsCloseTime = this.convertTimeToDayJs(tSpan.closes);

      const cmOpenTime = this.convertTimeToDayJs(newTimeSpan.opens);
      const cmCloseTime = this.convertTimeToDayJs(newTimeSpan.closes);

      if (
        tsOpenTime.toString() === cmOpenTime.toString() &&
        tsCloseTime.hour() === 0 &&
        tsCloseTime.minute() === 0 &&
        cmCloseTime.hour() === 0 &&
        cmCloseTime.minute() === 0
      ) {
        return true;
      }

      return tsOpenTime.toString() === cmOpenTime.toString() && tsCloseTime.toString() === cmCloseTime.toString();
    });

    return existingTimeSpan.length > 0;
  }

  convertTimeToDayJs(time: string): dayjs.Dayjs {
    if (time === '') return null;

    time = time.toLowerCase().replace(/ /g, '');
    if (time.includes('am') || time.includes('pm')) {
      return dayjs(time, 'hh:mma');
    }
    return dayjs(time, 'HH:mm');
  }

  dayOfWeekToString(day: DayOfWeek): string {
    switch (day) {
      case DayOfWeek.SUNDAY:
        return 'Sunday';
      case DayOfWeek.MONDAY:
        return 'Monday';
      case DayOfWeek.TUESDAY:
        return 'Tuesday';
      case DayOfWeek.WEDNESDAY:
        return 'Wednesday';
      case DayOfWeek.THURSDAY:
        return 'Thursday';
      case DayOfWeek.FRIDAY:
        return 'Friday';
      case DayOfWeek.SATURDAY:
        return 'Saturday';
      default:
        return '';
    }
  }
}
